import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { CommentService } from '../../../../../../services/comment.service';
import { DeleteCommentComponent } from './delete-comment/delete-comment.component';
import { ModalService } from '../../../../../../services/modal.service';
import { $comment, $manualAuditIssueComment } from '../../../../../../../../shared/constants/comment';
import { IManualEvaluationAuditListItem } from '../../../../../../../../shared/interfaces/manual-audit.interface';
import { LoadErrorHandler } from '../../../../../../services/load-error-handler';
import { IRequiredSecurity } from '../../../../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../../../../shared/constants/required-securities';
import { Origin } from '../../../../../../../../shared/interfaces/scan.interface';

@Component({
  selector: 'app-manual-audit-issue-comment-section',
  templateUrl: './manual-audit-issue-comment-section.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManualAuditIssueCommentSectionComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;

  @Input() public manualAuditFindingId: string;
  @Input() public workspaceId: string;
  @Input() public digitalPropertyId: string;
  @Input() public manualAuditIssueId: string;
  @Input() public evaluationScanId: string;
  @Input() public userOrigin: Origin;
  @Input() public evaluationOrigin: Origin;

  public comments: IManualEvaluationAuditListItem[];
  public createCommentRequiredSecurity: IRequiredSecurity;

  constructor(
    private commentService: CommentService,
    private changeDetector: ChangeDetectorRef,
    private modalService: ModalService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.subscriptions = new Subscription();
    this.comments = [];
    this.createCommentRequiredSecurity = RequiredSecurities.MT_Manual_Evaluation_Comment_Create;
  }

  private onGetManualAuditIssueCommentsSuccess(res: IManualEvaluationAuditListItem[]): void {
    this.comments = res;
    this.changeDetector.detectChanges();
  }

  private onGetManualAuditIssueCommentsError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('error_getting_documents', error, ManualAuditIssueCommentSectionComponent.name);
  }

  private onDeleteCommentError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('error_deleting_comment', error, ManualAuditIssueCommentSectionComponent.name);
  }

  private onEditCommentError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('error_posting_comment', error, ManualAuditIssueCommentSectionComponent.name);
  }

  public getComments(): Subscription {
    return this.commentService
      .getManualAuditIssueComments(this.evaluationScanId, this.manualAuditIssueId, this.workspaceId, this.digitalPropertyId)
      .subscribe({
        next: this.onGetManualAuditIssueCommentsSuccess.bind(this),
        error: this.onGetManualAuditIssueCommentsError.bind(this),
      });
  }

  public requestCommentDeletion(commentId: string): void {
    const requestedComment: IManualEvaluationAuditListItem = this.comments.find(
      (comment: IManualEvaluationAuditListItem) => comment[$comment._id] === commentId,
    );

    const modalComponentRef: ComponentRef<DeleteCommentComponent> = this.modalService.open(DeleteCommentComponent);

    modalComponentRef.instance.comment = requestedComment;
    this.subscriptions.add(modalComponentRef.instance.deleteCommentConfirmed.subscribe(this.deleteComment.bind(this)));
  }

  public editComment(event: { commentId: string; message: string }): void {
    this.subscriptions.add(
      this.commentService
        .editManualAuditIssueComment(
          this.workspaceId,
          this.digitalPropertyId,
          this.evaluationScanId,
          this.manualAuditIssueId,
          event.commentId,
          { [$manualAuditIssueComment.message]: event.message },
        )
        .subscribe({
          next: this.getComments.bind(this),
          error: (error: HttpErrorResponse): void => {
            this.onEditCommentError(error);
            this.getComments();
          },
        }),
    );
  }

  public deleteComment(commentId: string): void {
    this.subscriptions.add(
      this.commentService
        .deleteManualAuditIssueComment(
          this.workspaceId,
          this.digitalPropertyId,
          this.evaluationScanId,
          this.manualAuditIssueId,
          commentId,
        )
        .subscribe({
          next: this.getComments.bind(this),
          error: this.onDeleteCommentError.bind(this),
        }),
    );
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.getComments());
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
